import {
  SpaceBetween,
  TextContent,
  FormField,
  Badge,
  ExpandableSection,
  Table,
  StatusIndicator,
  FileUpload,
  Button,
  Alert,
  Box,
  Popover,
  PopoverProps,
  Select,
} from '@cloudscape-design/components';
import CompositionSafeTextarea from 'src/components/CompositionSafeTextarea';
import './PerformAuditQuestion.scss';
import SafeHtml from 'src/components/SafeHtml';
import ImageGallery from '../../ImageGallery/ImageGallery';
import FileDisplay from 'src/components/FileDisplay';
import TextEditor from 'src/components/textEditor/TextEditor';
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Divider from 'src/components/divider/Divider';
import AuditQuestionActionButton from '../AuditQuestionActionButton/AuditQuestionActionButton';
import { FeedbackForm } from './FeedbackForm';
import {
  AuditStatus,
  QuestionResultType,
  POLL_INTERVAL_MS,
} from 'src/contstant/commonLabels';
import { Audit } from 'src/types/helios/Audit';
import useUserProfile from 'src/hooks/useUserProfile';
import { useQuestionResponses } from 'src/store/questionResponses/useQuestionResponses';
import LastUpdatedIndicator from '../LastUpdatedIndicator';
import { useAppDispatch } from 'src/store/hooks';
import { pollQuestionResponsesApiThunk } from 'src/store/questionResponses/questionResponsesSlice';
import { getAuditQuestionResponseStatusIcon } from 'src/utils/generalUtils';
import { AuditQuestionItemResponse } from 'src/types/helios/QuestionResponse';
import UploadProgressIndicator from 'src/components/UploadProgressIndicator';
import { useMediaUpload } from 'src/hooks/useMediaUpload';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { useAudits } from 'src/store/audits/useAudits';
import { Question, ResponseType } from 'src/types/helios/Question';
import {
  getAllMediaIdsfromQuestionForAudit,
  getDocumentsByField,
} from 'src/utils/mediaUtils';
import { useMedia } from 'src/store/media/useMedia';
import {
  updateAuditToInprogress,
  updateQuestionAnswer as updateQuestionAnswerHelper,
  getMainQuestionStatus,
  handleFileUpload as handleFileUploadHelper,
  updateResponseAfterMediaDeletion,
  getImageFiles,
  getSubQuestionImageFiles,
  removeMediaResponses,
  getDocumentFiles,
  getSubQuestionDocumentFiles,
  ImageGalleryItem,
} from '../../../helper/performAuditHelpers';
import { AUDIT_UPLOAD_ACCEPT_TYPES } from 'src/contstant/fileConstants';
import FieldRequirementLabel from './FieldRequirementLabel';

// Constants
export const AUTO_SAVE_DELAY_MS = 10000;

import { getQuestionContent } from 'src/helper/questionHelper';
import { useFeedbackQuery } from 'src/data/api/helios-service-ts/feedback/queries/useFeedbackQuery';
import { useMutationState } from '@tanstack/react-query';
import { feedbackKeys } from 'src/data/api/helios-service-ts/feedback/queries/keys';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import { useListTasksQuery } from 'src/data/api/helios-service-ts/task/queries/useListTasksQuery';
import { Task, TaskStatus, ReasonCategory } from '@amzn/gbsa-helios-typescript-client';
import { useListReasonsQuery } from 'src/data/api/helios-service-ts/reason/queries/useListReasonsQuery';
import {
  shouldShowNaReasonDropdown,
  getQuestionViolations,
} from 'src/utils/auditClosureValidationUtils';
import TaskDraftPanel from './TaskDraftPanel';
import DataCollectionInput from './DataCollectionInput';
// Handle type exposed to parent via ref
export interface PerformAuditQuestionViewHandle {
  /** Saves any unsaved comments immediately. */
  flushPendingComments: () => void;
}

// Props interface for the component
interface PerformAuditQuestionViewProps {
  audit?: Audit; // The audit being performed
  isReadOnlyMode: boolean; // Whether the view is read-only
  disableReason?: string;
}

/**
 * Component for displaying and managing audit question responses
 * Handles question display, answer selection, comments, and file uploads
 */
const PerformAuditQuestionView = forwardRef<
  PerformAuditQuestionViewHandle,
  PerformAuditQuestionViewProps
>(({ audit, isReadOnlyMode, disableReason }, ref) => {
  // Feature flags
  const {
    enableAuditTaskDrafts,
    enableDivisionalConfiguration,
    enableMultiAssignee,
    enableEnhancedResponseTypes,
  } = useFeatureFlags();

  const dispatch = useAppDispatch();

  // Get user profile information for API calls
  const { getUserProgramId, getUserId, getUserSelectedLanguage } = useUserProfile();
  const [translationError, setTranslationError] = useState('');
  const [draftErrorMessage, setDraftErrorMessage] = useState<string | null>(null);
  // State for the currently displayed question
  const [currentQuestion, setCurrentQuestionData] = useState<Question>();
  const [currentQuestionResponse, setCurrentQuestionResponse] =
    useState<AuditQuestionItemResponse>();
  const [isDataSaved, setDataSaved] = useState<boolean>(true);
  const [lastQuestionResult, setLastQuestionResult] = useState('');
  const [localComments, setLocalComments] = useState<Record<string, string>>({});
  // Track NA reason selections keyed by questionId or subQuestionId
  const [localNaReasons, setLocalNaReasons] = useState<
    Record<string, { reasonId: string; label: string }>
  >({});
  // ERT: Track data-collection response values keyed by subQuestionId
  // Only used for data-collection types (TEXT, NUMERIC, etc.) — PASS_FAIL uses result
  const [localResponseValues, setLocalResponseValues] = useState<Record<string, string>>(
    {},
  );
  // Track the comment values that were last sent to the API for the current question
  // This helps detect if user made new changes while API call was in progress
  const lastSavedCommentsRef = useRef<Record<string, string>>({});
  // Create ref array to store DOM references for each question
  const questionRefs = useRef<HTMLDivElement | null>();
  const feedbackPopoverRef = useRef<PopoverProps.Ref | null>(null);
  // Hook for updating audit status
  const { updateAudit, updateAuditApiStatus } = useAudits();
  const { listMediaByIds, mediaList, listMediaByIdsApiStatus } = useMedia();

  // Hook for fetching NA reasons for the audit's division (gated by feature flag)
  const { data: reasonsData } = useListReasonsQuery(
    {
      businessUnitId: audit?.businessUnitId || audit?.businessUnit || '',
      category: ReasonCategory.NA_REASON,
      divisionName: audit?.divisionName || '',
      activeOnly: true,
    },
    { enabled: enableDivisionalConfiguration && !!audit?.divisionName },
  );
  const naReasons = useMemo(() => reasonsData?.reasons ?? [], [reasonsData]);

  // Hook for handling file uploads and media management
  const {
    isUploading, // Upload progress state
    uploadProgress, // Upload progress percentage
    uploadfiles, // Function to upload files
    deleteMedia, // Function to delete media
    uploadMediaApiStatus, // Upload API status
    clearMediaAllApiStatus, // Clear all media API statuses
    deleteMediaApiStatus, // Delete media API status
  } = useMediaUpload();

  // Hook for managing question responses
  const {
    currentQuestionIndex, // Index of current question
    auditQuestionResponses, // All question responses
    auditQuestionsData, // Question data
    updateQuestionResponse, // Function to update responses
    updateQuestionResponseApiStatus, // API status for updates
    updateResponseErrorCode, // HTTP error code (409 for version conflict)
    resetStatus, // Reset API status
    updateQuestionResponseList,
    searchQuestionsApiStatus,
    setCurrentQuestion,
    filteredQuestionIds, // Filtered question IDs from pie chart filter
  } = useQuestionResponses();

  // Get the filtered questions list - if filter is applied, use filtered IDs, otherwise use all questions
  const displayedQuestions = useMemo(() => {
    if (filteredQuestionIds === null) {
      return auditQuestionsData.questions;
    }
    return auditQuestionsData.questions.filter((q) =>
      filteredQuestionIds.includes(q.questionId),
    );
  }, [auditQuestionsData.questions, filteredQuestionIds]);

  // Effect to auto-scroll to selected question when index changes
  useEffect(() => {
    setLastQuestionResult('');

    // Check if the current question element exists
    if (questionRefs.current) {
      // Scroll to the selected question
      questionRefs.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
    }
  }, [currentQuestionIndex]);

  // Effect: Auto-advance to next question when current question is answered with PASS
  // Provides smooth user experience by automatically moving forward on successful answers.
  // Suppressed when the PASS response has unmet mandatory requirements (comment/evidence
  // on the main question or any sub-question), so the user stays on the page to complete them.
  useEffect(() => {
    // Do not auto-advance if the current question still has unmet mandatory requirements,
    // otherwise the missing-input prompt would not be obvious to the auditor.
    const hasUnmetRequirements =
      !!currentQuestion &&
      getQuestionViolations(
        currentQuestion,
        currentQuestionResponse,
        undefined,
        enableEnhancedResponseTypes,
      ).length > 0;

    // Check if the last question result matches the current response result AND both are PASS
    if (
      lastQuestionResult === currentQuestionResponse?.responseResult &&
      currentQuestionResponse?.responseResult === QuestionResultType.PASS &&
      !hasUnmetRequirements
    ) {
      // Create a delayed timeout to automatically advance to the next question after 500ms
      const timeoutId = setTimeout(() => {
        // Move to the next question, ensuring we don't exceed the total number of displayed questions
        setCurrentQuestion(
          Math.min(displayedQuestions.length - 1, currentQuestionIndex + 1),
        );
      }, 500);
      // Cleanup function to clear the timeout if the component unmounts or dependencies change
      return () => clearTimeout(timeoutId);
    }
    // Re-run this effect when lastQuestionResult or currentQuestionResponse changes
  }, [
    lastQuestionResult,
    currentQuestionResponse,
    currentQuestion,
    displayedQuestions.length,
  ]);

  /**
   * Effect: Synchronize current question response and comments with Redux state
   * Updates currentQuestionResponse on every Redux update to reflect API changes
   * Only syncs localComments when data is saved (no pending local changes)
   */
  useEffect(() => {
    const response = auditQuestionResponses.find(
      (item) => item.questionId === currentQuestion?.questionId,
    );

    // Always update response to reflect status changes immediately
    setCurrentQuestionResponse(response);

    // Clear the conflict lock when fresh data arrives (poll completed)
    if (response && conflictLockRef.current) {
      conflictLockRef.current = false;
    }

    // Only sync comments from Redux if data is saved (no pending local changes)
    // This prevents overwriting user's unsaved changes while allowing updates from Redux
    if (response && currentQuestion && isDataSaved) {
      const commentKey = currentQuestion.questionId;
      setLocalComments((prev) => {
        const newComments = { ...prev };

        // Sync main question comment
        newComments[commentKey] = response.response?.comment || '';

        // Sync sub-question comments
        response.subResponses?.forEach((subResponse) => {
          const subCommentKey = subResponse.subQuestionId;
          newComments[subCommentKey] = subResponse.comment || '';
        });

        return newComments;
      });
    }
  }, [auditQuestionResponses, currentQuestion?.questionId, isDataSaved]);

  // ERT: Sync localResponseValues from Redux when question/response changes (same pattern as localComments)
  useEffect(() => {
    const response = auditQuestionResponses.find(
      (item) => item.questionId === currentQuestion?.questionId,
    );
    if (response && currentQuestion && isDataSaved) {
      setLocalResponseValues((prev) => {
        const newValues = { ...prev };
        response.subResponses?.forEach((subResponse) => {
          if (subResponse.responseValue !== undefined) {
            newValues[subResponse.subQuestionId] = subResponse.responseValue;
          }
        });
        return newValues;
      });
    }
  }, [auditQuestionResponses, currentQuestion?.questionId, isDataSaved]);

  /**
   * Gets the business unit ID from the audit object
   * Handles both businessUnitId and businessUnit properties for backward compatibility
   * @returns {string} The business unit identifier
   */
  const getAuditBusinessUnitId = () => {
    return audit?.businessUnitId || audit?.businessUnit || '';
  };

  /**
   * Extracts comments for the current question and its sub-questions from localComments
   * @returns {Record<string, string>} Object with question/sub-question IDs as keys and comments as values
   */
  const getCurrentQuestionComments = useCallback((): Record<string, string> => {
    if (!currentQuestion) return {};

    const mainCommentKey = currentQuestion.questionId;
    const comments: Record<string, string> = {
      [mainCommentKey]: localComments[mainCommentKey] || '',
    };

    currentQuestionResponse?.subResponses?.forEach((subResponse) => {
      comments[subResponse.subQuestionId] =
        localComments[subResponse.subQuestionId] || '';
    });

    return comments;
  }, [currentQuestion, currentQuestionResponse, localComments]);

  /**
   * Builds the updated response object with merged comments from localComments
   * Handles fallback to existing response comments if localComments not synced
   * Excludes mediaResponses to avoid sending presigned URLs back to backend
   * @returns {AuditQuestionItemResponse} Updated response with current comments
   */
  const buildUpdatedResponseWithComments = useCallback((): AuditQuestionItemResponse => {
    const mainCommentKey = currentQuestion!.questionId;
    const mainComment = (
      localComments[mainCommentKey] ??
      currentQuestionResponse!.response?.comment ??
      ''
    ).trim();

    // Get NA reason for main question (local selection takes precedence over saved)
    const mainNaReason = localNaReasons[currentQuestion!.questionId];

    // Clean mediaResponses from response using utility function
    const responseWithoutMedia = removeMediaResponses(currentQuestionResponse!);

    // Effective main NA reason (local selection or previously saved) used for propagation
    const effectiveMainNaReasonLabel =
      mainNaReason?.label || responseWithoutMedia.response?.naReason;
    const effectiveMainNaReasonId =
      mainNaReason?.reasonId || responseWithoutMedia.response?.naReasonId;

    return {
      ...responseWithoutMedia,
      response: {
        ...responseWithoutMedia.response,
        // Only include comment if it has content (API requires min length of 1 if present)
        // Setting to undefined causes JSON.stringify to omit the field
        comment: mainComment || undefined,
        // Include NA reason fields (fall back to existing saved values to avoid wiping them)
        naReason:
          mainNaReason?.label || responseWithoutMedia.response?.naReason || undefined,
        naReasonId:
          mainNaReason?.reasonId ||
          responseWithoutMedia.response?.naReasonId ||
          undefined,
      },
      subResponses: responseWithoutMedia.subResponses?.map((subResponse) => {
        const subComment = (
          localComments[subResponse.subQuestionId] ??
          subResponse.comment ??
          ''
        ).trim();

        // ERT: Merge local responseValue for data-collection subs
        const localValue = localResponseValues[subResponse.subQuestionId];
        const responseValue =
          localValue !== undefined ? localValue : subResponse.responseValue;

        // Get NA reason for this sub-question
        const subNaReason = localNaReasons[subResponse.subQuestionId];

        // Resolve the sub-question's own NA reason (local selection or previously saved)
        const ownNaReasonLabel = subNaReason?.label || subResponse.naReason;
        const ownNaReasonId = subNaReason?.reasonId || subResponse.naReasonId;

        // Propagate the main question's NA reason to NA sub-questions that have no reason of their own
        const shouldInheritMainReason =
          subResponse.result === QuestionResultType.NA && !ownNaReasonId;

        return {
          ...subResponse,
          // Only include comment if it has content (API requires min length of 1 if present)
          // Setting to undefined causes JSON.stringify to omit the field
          comment: subComment || undefined,
          // ERT: Include data-collection response value
          responseValue: responseValue || undefined,
          // Use the sub-question's own reason, or inherit the main reason when NA and no own reason
          naReason:
            ownNaReasonLabel ||
            (shouldInheritMainReason ? effectiveMainNaReasonLabel : undefined) ||
            undefined,
          naReasonId:
            ownNaReasonId ||
            (shouldInheritMainReason ? effectiveMainNaReasonId : undefined) ||
            undefined,
        };
      }),
    };
  }, [
    currentQuestion,
    currentQuestionResponse,
    localComments,
    localNaReasons,
    localResponseValues,
  ]);

  /**
   * Effect: Set current question based on the current question index
   * Updates whenever the question index or displayed questions change
   * Uses displayedQuestions which respects the pie chart filter
   */
  useEffect(() => {
    // Only set question if questions data is available
    if (displayedQuestions.length > 0) {
      // Ensure currentQuestionIndex is within bounds of filtered list
      const safeIndex = Math.min(currentQuestionIndex, displayedQuestions.length - 1);
      const questionToDisplay = displayedQuestions[safeIndex];

      if (questionToDisplay) {
        setCurrentQuestionData(questionToDisplay);

        const mediaIds = getAllMediaIdsfromQuestionForAudit(questionToDisplay);

        if (mediaIds.length > 0) {
          listMediaByIds({
            programId: getUserProgramId(),
            mediaIds: mediaIds,
            businessUnit: getAuditBusinessUnitId(),
          });
        }
      }

      // Save unsaved changes before switching to new question
      // This runs before currentQuestion state is updated, so it saves the OLD question
      if (!isDataSaved) {
        saveQuestionResponse();
      }
    }
  }, [currentQuestionIndex, displayedQuestions]);

  // Track whether a version conflict is active — prevents saves until fresh data arrives
  const conflictLockRef = useRef(false);

  // Track whether a save API call is currently in-flight (ref avoids stale closure in setTimeout)
  const isSavingRef = useRef(false);

  /**
   * Saves the current question response to the backend
   * Updates the audit status to 'in progress' if not already
   * Skips saving in read-only mode, if no response exists, or if conflict-locked
   */
  const saveQuestionResponse = () => {
    if (!currentQuestionResponse || isReadOnlyMode || !currentQuestion) {
      return;
    }

    // Don't save while conflict lock is active — token is stale
    if (conflictLockRef.current) {
      return;
    }

    // Read the latest lastUpdatedAt from the Redux store (not local state) to avoid stale tokens
    const freshResponse = auditQuestionResponses.find(
      (item) => item.questionId === currentQuestion.questionId,
    );
    const expectedLastUpdatedAt =
      freshResponse?.lastUpdatedAt || currentQuestionResponse.lastUpdatedAt;

    // Build updated response with current comments merged
    const updatedResponse = {
      ...buildUpdatedResponseWithComments(),
      respondedBy: getUserId(),
      expectedLastUpdatedAt,
    };

    // Determine whether to send responseResult or let backend derive:
    // - Flag OFF: original behavior (send undefined when subs exist)
    // - Flag ON + P/F subs exist: send undefined → backend derives from sub results
    // - Flag ON + no P/F subs (conditional/self-scoring): preserve existing
    //   responseResult because backend mapper overwrites DDB before derivation
    //   short-circuits, losing the parent answer.
    let responseResult: string | undefined;
    if (enableEnhancedResponseTypes) {
      const hasPassFailSubs = updatedResponse?.subResponses?.some(
        (sub) => !sub.responseType || sub.responseType === 'PASS_FAIL',
      );
      responseResult = hasPassFailSubs ? undefined : updatedResponse.responseResult;
    } else {
      responseResult =
        updatedResponse?.subResponses?.length > 0
          ? undefined
          : updatedResponse.responseResult;
    }

    // Snapshot current question's comments being sent to API
    lastSavedCommentsRef.current = getCurrentQuestionComments();

    updateQuestionResponse({ ...updatedResponse, responseResult });
    updateAuditToInprogress(audit!, updateAudit, getUserId);
  };

  // Stable ref for getCurrentQuestionComments to avoid re-running the API
  // status effect on every keystroke (localComments changes → callback ref changes)
  const getCurrentQuestionCommentsRef = useRef(getCurrentQuestionComments);
  getCurrentQuestionCommentsRef.current = getCurrentQuestionComments;

  // Expose imperative handle to parent (used by Close Audit to flush pending comments)
  useImperativeHandle(
    ref,
    () => ({
      flushPendingComments: () => {
        if (!isDataSaved) {
          saveQuestionResponse();
        }
      },
    }),
    [isDataSaved, saveQuestionResponse],
  );

  /**
   * Effect: Handle API response status changes
   * - Marks data as saved when question response update succeeds (only if no new changes were made)
   * - Auto-saves pending changes when audit is completed
   */
  useEffect(() => {
    if (updateQuestionResponseApiStatus.isSuccess) {
      // Check if current question's comments have changed since we sent the API request
      const currentComments = getCurrentQuestionCommentsRef.current();
      const commentsMatch =
        JSON.stringify(currentComments) === JSON.stringify(lastSavedCommentsRef.current);
      setDataSaved(commentsMatch);
      resetStatus();
    }
    if (
      updateAuditApiStatus.isSuccess &&
      updateAuditApiStatus.updatedAudit?.status === AuditStatus.COMPLETED
    ) {
      if (!isDataSaved) {
        saveQuestionResponse();
      }
    }
  }, [updateQuestionResponseApiStatus.isSuccess, updateAuditApiStatus.isSuccess]);

  /**
   * Effect: Keep isSavingRef in sync with the API loading state.
   * This ref is read inside the auto-save setTimeout callback where a direct
   * closure over isLoading would be stale (captured when the effect ran, not
   * when the timer fires).
   */
  useEffect(() => {
    isSavingRef.current = updateQuestionResponseApiStatus.isLoading;
  }, [updateQuestionResponseApiStatus.isLoading]);

  /**
   * Effect: On version conflict (409), reset state to prevent infinite retries.
   * The error is already shown via ApiStatusFlashBar. We mark data as saved
   * to stop auto-save from retrying, and dispatch an immediate silent poll to get fresh data.
   */
  useEffect(() => {
    if (enableMultiAssignee && updateResponseErrorCode === 409) {
      conflictLockRef.current = true;
      setDataSaved(true);
      dispatch(
        pollQuestionResponsesApiThunk({
          auditId: audit?.auditId ?? '',
          businessUnitId: audit?.businessUnitId || audit?.businessUnit || '',
        }),
      );
    }
  }, [updateResponseErrorCode]);

  /**
   * Effect: Debounced auto-save for comments
   * Automatically saves comments 10 seconds after user stops typing
   * Prevents data loss if user navigates away or closes browser
   */
  useEffect(() => {
    // Skip auto-save if:
    // - Data is already saved
    // - In read-only mode
    // - No response exists
    // - API call is already in progress
    if (
      isDataSaved ||
      isReadOnlyMode ||
      !currentQuestionResponse ||
      updateQuestionResponseApiStatus.isLoading
    ) {
      return;
    }

    // Set up a timer to auto-save after 10 seconds of inactivity
    const autoSaveTimer = setTimeout(() => {
      // Guard: do not fire auto-save if a save is already in-flight.
      // Without this check, a rapid answer-click (FAIL/N/A) followed by
      // this timer expiring can send two concurrent saves with the same
      // expectedLastUpdatedAt token, causing ConditionalCheckFailed (409)
      // and a UI revert of the user's selection. See D493273765.
      // Uses a ref (not the closure value) because the closure captures
      // isLoading=false at effect-run time — it would never be true here.
      if (isSavingRef.current) {
        return;
      }
      saveQuestionResponse();
    }, AUTO_SAVE_DELAY_MS);

    // Cleanup: Clear the timer if user types again or component unmounts
    return () => {
      clearTimeout(autoSaveTimer);
    };
  }, [
    isDataSaved,
    localComments,
    localResponseValues,
    isReadOnlyMode,
    currentQuestionResponse,
    updateQuestionResponseApiStatus.isLoading,
  ]);

  /**
   * Effect: Immediate save when a selection-based data-collection value changes
   * Fires only when pendingImmediateSaveRef is true (set by setResponseValueImmediate).
   * Same pattern as the NA reason immediate save below.
   */
  useEffect(() => {
    if (
      !pendingImmediateSaveRef.current ||
      isDataSaved ||
      isReadOnlyMode ||
      !currentQuestionResponse ||
      updateQuestionResponseApiStatus.isLoading
    ) {
      return;
    }
    pendingImmediateSaveRef.current = false;
    saveQuestionResponse();
  }, [localResponseValues]);

  /**
   * Effect: Immediate save when NA reason is selected
   * Unlike comments which debounce for 10s, NA reason selection triggers save immediately
   * Only fires when user actively selects a reason (not on sync from response data)
   */
  const prevNaReasonsRef = useRef<string>(JSON.stringify(localNaReasons));
  useEffect(() => {
    const currentSerialized = JSON.stringify(localNaReasons);
    // Only trigger if localNaReasons content actually changed
    if (
      prevNaReasonsRef.current === currentSerialized ||
      isDataSaved ||
      isReadOnlyMode ||
      !currentQuestionResponse ||
      updateQuestionResponseApiStatus.isLoading
    ) {
      prevNaReasonsRef.current = currentSerialized;
      return;
    }
    prevNaReasonsRef.current = currentSerialized;

    // Immediate save
    saveQuestionResponse();
  }, [localNaReasons]);

  /**
   * Effect: Handle media deletion completion
   * Updates the question response to remove references to deleted media
   * Only processes successful deletions in non-read-only mode
   */
  useEffect(() => {
    if (
      !deleteMediaApiStatus.isSuccess ||
      !deleteMediaApiStatus.mediaId ||
      !currentQuestion ||
      isReadOnlyMode
    ) {
      return;
    }

    // Determine if this media belongs to a sub-question
    const subQuestionId = currentQuestionResponse?.subResponses?.find((subResponse) =>
      subResponse.mediaIds?.includes(deleteMediaApiStatus.mediaId!),
    )?.subQuestionId;

    updateResponseAfterMediaDeletion(
      deleteMediaApiStatus.mediaId,
      updateQuestionResponse,
      getUserId(),
      currentQuestionResponse,
      subQuestionId,
      enableEnhancedResponseTypes,
    );
  }, [deleteMediaApiStatus]);

  /**
   * Updates the answer for a question or sub-question
   * Handles both main questions and sub-questions based on provided parameters
   * Updates audit status to 'in progress' after answer change
   * @param {string} questionId - The ID of the main question
   * @param {string} [result] - The answer result (PASS/FAIL/NA)
   * @param {string} [subQuestionId] - Optional sub-question ID for sub-question answers
   */
  const updateQuestionAnswer = (
    questionId: string,
    result?: string,
    subQuestionId?: string,
  ) => {
    if (!currentQuestionResponse || isReadOnlyMode || !currentQuestion) {
      return;
    }

    // Don't save while conflict lock is active — token is stale
    if (conflictLockRef.current) {
      return;
    }

    // Clear NA reason when answer changes away from NA
    if (result !== QuestionResultType.NA) {
      clearNaReason(subQuestionId);
    }

    // Build updated response with current comments merged
    const updatedResponse = buildUpdatedResponseWithComments();

    // Snapshot current question's comments being sent to API
    lastSavedCommentsRef.current = getCurrentQuestionComments();

    updateQuestionAnswerHelper(
      questionId,
      updatedResponse,
      updateQuestionResponse,
      getUserId(),
      currentQuestion,
      result,
      subQuestionId,
    );
    updateAuditToInprogress(audit!, updateAudit, getUserId);
    if (result) {
      setLastQuestionResult(result);
    }
  };

  /**
   * Renders the sub-questions section in an expandable container
   * Each sub-question displays with its own answer buttons, status, and comment field
   * Returns null if no sub-questions exist for the current question
   * @returns {JSX.Element | null} The rendered sub-questions section or null
   */
  const renderSubQuestions = () => {
    if (!currentQuestion) {
      return null;
    }
    const subQuestions = getQuestionContent(
      currentQuestion,
      getUserSelectedLanguage(),
    ).subQuestions;
    if (!subQuestions?.length) return null;

    // ERT: For conditional parents, determine which subs to display
    const isConditional =
      enableEnhancedResponseTypes && currentQuestion.hasConditionalSubQuestions;
    const parentResult = currentQuestionResponse?.responseResult;

    // If conditional and parent not answered or NA, show message instead of subs
    if (isConditional && (!parentResult || parentResult === 'NA')) {
      return (
        <ExpandableSection
          data-testid="sub-questions-section"
          variant="default"
          headerText="Sub Questions:"
          defaultExpanded
        >
          <Box
            data-testid="conditional-awaiting-parent"
            textAlign="center"
            color="text-body-secondary"
            padding={{ vertical: 'l' }}
          >
            Answer the parent question (Pass or Fail) to reveal sub-questions.
          </Box>
        </ExpandableSection>
      );
    }

    // If conditional and parent answered PASS/FAIL, filter by showWhenParentIs
    const displayedSubQuestions = isConditional
      ? subQuestions.filter((sq) => {
          const subResp = currentQuestionResponse?.subResponses?.find(
            (sr) => sr.subQuestionId === sq.subQuestionId,
          );
          return (
            subResp?.showWhenParentIs === parentResult ||
            subResp?.showWhenParentIs === 'BOTH'
          );
        })
      : subQuestions;

    if (!displayedSubQuestions.length) return null;

    return (
      <ExpandableSection
        data-testid="sub-questions-section"
        variant="default"
        headerText="Sub Questions:"
        defaultExpanded
      >
        <SpaceBetween data-testid="sub-questions-spacer" size="l">
          {displayedSubQuestions.map((subQuestion, index) => {
            // Find the response for this specific sub-question
            const subResponse = currentQuestionResponse?.subResponses?.find(
              (item) => item.subQuestionId === subQuestion.subQuestionId,
            );
            const subQuestionId = subQuestion.subQuestionId;
            const isDataCollectionSub =
              enableEnhancedResponseTypes &&
              !!subQuestion.responseType &&
              subQuestion.responseType !== ResponseType.PASS_FAIL;
            return (
              <div
                data-testid={`sub-question-container-${index}`}
                key={index}
                className={`sub-question-item ${index % 2 === 0 ? 'sub-question-even' : ''}`}
              >
                <div className="sub-question-header">
                  <SpaceBetween
                    data-testid={`sub-question-content-${index}`}
                    size="m"
                    direction="horizontal"
                  >
                    <StatusIndicator
                      data-testid={`sub-question-status-${index}`}
                      type={getAuditQuestionResponseStatusIcon(
                        enableEnhancedResponseTypes &&
                          subQuestion.responseType &&
                          subQuestion.responseType !== ResponseType.PASS_FAIL
                          ? getResponseValue(subQuestionId || '')
                            ? 'PASS'
                            : undefined
                          : subResponse?.result,
                      )}
                    />
                    <TextContent data-testid={`sub-question-text-${index}`}>
                      <h5>{subQuestion.title}</h5>
                      <small>
                        Sub Question ID: <strong>{subQuestion.subQuestionId}</strong>
                      </small>
                      <SafeHtml
                        html={subQuestion.instruction}
                        mediaResponse={
                          subQuestion.mediaIds
                            ? mediaList.filter((m) =>
                                subQuestion.mediaIds!.includes(m.media.mediaId),
                              )
                            : []
                        }
                      />
                    </TextContent>
                  </SpaceBetween>
                </div>
                {/* ERT: Data-collection subs get vertical layout (Response → Comments → Attachments) */}
                {enableEnhancedResponseTypes &&
                subQuestion.responseType &&
                subQuestion.responseType !== ResponseType.PASS_FAIL ? (
                  <Box margin={{ top: 'm' }}>
                    <SpaceBetween size="m">
                      {/* Response input with header/label/helper */}
                      <DataCollectionInput
                        data-testid={`sub-question-data-input-${index}`}
                        responseType={subQuestion.responseType}
                        value={getResponseValue(subQuestionId || '')}
                        onChange={(value) => {
                          if (!subQuestionId) return;
                          // TEXT/NUMERIC: debounced save (user is typing)
                          if (
                            subQuestion.responseType === ResponseType.TEXT ||
                            subQuestion.responseType === ResponseType.NUMERIC
                          ) {
                            setResponseValue(subQuestionId, value);
                          } else {
                            // SINGLE_SELECT, MULTI_SELECT, YES_NO, USER_ALIAS: immediate save
                            setResponseValueImmediate(subQuestionId, value);
                          }
                        }}
                        options={subQuestion.options}
                        readOnly={isReadOnlyMode}
                        programId={getUserProgramId()}
                      />

                      {/* Comments (below response) */}
                      <FormField
                        data-testid={`sub-question-comments-field-${index}`}
                        label={
                          <FieldRequirementLabel
                            fieldName="Comments"
                            alwaysRequired={
                              !!subQuestion.requireComment &&
                              !!getResponseValue(subQuestionId || '') &&
                              currentQuestionResponse?.responseResult !== 'NA'
                            }
                          />
                        }
                      >
                        <CompositionSafeTextarea
                          data-testid={`sub-question-comments-textarea-${index}`}
                          placeholder="Add your comments here..."
                          readOnly={isReadOnlyMode}
                          value={getComments(subQuestionId)}
                          onChange={(e) => {
                            setComments(e.detail.value, subQuestionId);
                          }}
                        />
                      </FormField>
                    </SpaceBetween>
                  </Box>
                ) : (
                  /* PASS_FAIL subs: existing side-by-side layout (comments + action buttons) */
                  <div
                    className="sub-question-comments-row"
                    style={{ marginTop: '12px' }}
                  >
                    <div className="sub-question-comments-field">
                      <FormField
                        data-testid={`sub-question-comments-field-${index}`}
                        label={
                          <FieldRequirementLabel
                            fieldName="Comments"
                            requireOnFail={subQuestion.requireCommentOnFail}
                            requireOnPass={subQuestion.requireCommentOnPass}
                            result={subResponse?.result}
                          />
                        }
                      >
                        <CompositionSafeTextarea
                          data-testid={`sub-question-comments-textarea-${index}`}
                          placeholder="Add your comments here..."
                          readOnly={isReadOnlyMode}
                          value={getComments(subQuestionId)}
                          onChange={(e) => {
                            setComments(e.detail.value, subQuestionId);
                          }}
                        />
                      </FormField>
                    </div>
                    <div className="sub-question-actions">
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <AuditQuestionActionButton
                          data-testid={`sub-question-action-button-${index}`}
                          disable={isReadOnlyMode}
                          ukey={subQuestionId || ''}
                          isLoading={updateQuestionResponseApiStatus.isLoading}
                          selectedAnswer={subResponse?.result}
                          onAnswerClick={(result?: QuestionResultType) =>
                            updateQuestionAnswer(
                              currentQuestion!.questionId,
                              result,
                              subQuestionId,
                            )
                          }
                          disabledReason={disableReason}
                        />
                        {enableMultiAssignee && subResponse?.result && (
                          <LastUpdatedIndicator
                            lastUpdatedBy={
                              subResponse.respondedBy ||
                              currentQuestionResponse?.lastUpdatedBy
                            }
                            lastUpdatedAt={
                              subResponse.respondedAt ||
                              currentQuestionResponse?.lastUpdatedAt
                            }
                          />
                        )}
                      </SpaceBetween>
                    </div>
                  </div>
                )}
                {/* NA Reason dropdown for sub-question */}
                {enableDivisionalConfiguration &&
                  naReasons.length > 0 &&
                  subQuestionId &&
                  shouldShowNaReasonDropdown('sub', {
                    mainResponseResult: currentQuestionResponse?.responseResult,
                    mainNaReasonId: currentQuestionResponse?.response?.naReasonId,
                    localMainNaReason: localNaReasons[currentQuestion!.questionId],
                    subResponseResult: subResponse?.result,
                    subNaReasonId:
                      localNaReasons[subQuestionId]?.reasonId || subResponse?.naReasonId,
                  }) && (
                    <Box margin={{ top: 's' }}>
                      <FormField
                        data-testid={`sub-question-na-reason-field-${index}`}
                        label="Not Applicable Reason"
                        errorText={
                          !localNaReasons[subQuestionId] &&
                          !subResponse?.naReasonId &&
                          !isReadOnlyMode
                            ? 'Please select a Not Applicable reason before closing the audit'
                            : undefined
                        }
                      >
                        {isReadOnlyMode ? (
                          <Box>
                            {localNaReasons[subQuestionId]?.label ||
                              subResponse?.naReason ||
                              '-'}
                          </Box>
                        ) : (
                          <Select
                            data-testid={`sub-question-na-reason-select-${index}`}
                            selectedOption={
                              localNaReasons[subQuestionId]
                                ? {
                                    label: localNaReasons[subQuestionId].label,
                                    value: localNaReasons[subQuestionId].reasonId,
                                  }
                                : subResponse?.naReasonId
                                  ? {
                                      label: subResponse.naReason || '',
                                      value: subResponse.naReasonId,
                                    }
                                  : null
                            }
                            onChange={({ detail }) => {
                              if (
                                detail.selectedOption.value &&
                                detail.selectedOption.label
                              ) {
                                setNaReason(
                                  detail.selectedOption.value,
                                  detail.selectedOption.label,
                                  subQuestionId,
                                );
                              }
                            }}
                            options={naReasons.map((r) => ({
                              label: r.label!,
                              value: r.reasonId!,
                            }))}
                            placeholder="Select Not Applicable reason"
                            filteringType="auto"
                          />
                        )}
                      </FormField>
                    </Box>
                  )}
                <Box margin={{ top: 'm' }}>
                  <SpaceBetween size="m">
                    <FormField
                      data-testid={`sub-question-attachments-field-${index}`}
                      label={
                        enableEnhancedResponseTypes &&
                        subQuestion.responseType &&
                        subQuestion.responseType !== ResponseType.PASS_FAIL ? (
                          <FieldRequirementLabel
                            fieldName="Attachments"
                            alwaysRequired={
                              !!subQuestion.requireAttachment &&
                              !!getResponseValue(subQuestionId || '') &&
                              currentQuestionResponse?.responseResult !== 'NA'
                            }
                          />
                        ) : (
                          <FieldRequirementLabel
                            fieldName="Attachments"
                            requireOnFail={subQuestion.requireEvidenceOnFail}
                            requireOnPass={subQuestion.requireEvidenceOnPass}
                            result={subResponse?.result}
                          />
                        )
                      }
                    >
                      {!isReadOnlyMode && subQuestionId && (
                        <FileUpload
                          data-testid={`sub-question-file-upload-${index}`}
                          accept={AUDIT_UPLOAD_ACCEPT_TYPES}
                          onChange={({ detail }) =>
                            handleFileUpload(detail.value, subQuestionId)
                          }
                          value={[]}
                          i18nStrings={{
                            uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
                            dropzoneText: (e) =>
                              e ? 'Drop files to upload' : 'Drop file to upload',
                            removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                            limitShowFewer: 'Show fewer files',
                            limitShowMore: 'Show more files',
                            errorIconAriaLabel: 'Error',
                          }}
                          multiple
                          showFileSize
                          showFileThumbnail
                        />
                      )}
                    </FormField>
                    {subQuestionId && (
                      <ImageGallery
                        data-testid={`sub-question-image-gallery-${index}`}
                        items={getSubQuestionImageFiles(
                          subQuestionId,
                          currentQuestionResponse,
                        )}
                        readOnly={isReadOnlyMode}
                        loading={deleteMediaApiStatus.isLoading}
                        onDelete={(mediaId) =>
                          deleteMedia({
                            programId: getUserProgramId(),
                            deletedBy: getUserId(),
                            mediaId,
                          })
                        }
                      />
                    )}
                    {subQuestionId && (
                      <FileDisplay
                        data-testid={`sub-question-file-display-${index}`}
                        files={getSubQuestionDocumentFiles(
                          subQuestionId,
                          currentQuestionResponse,
                        )}
                        readonly={isReadOnlyMode}
                        onRemove={(mediaId) =>
                          deleteMedia({
                            programId: getUserProgramId(),
                            deletedBy: getUserId(),
                            mediaId,
                          })
                        }
                      />
                    )}
                    {/* Task Draft Panel for sub-question - shown on FAIL (P/F) or when answered (data-collection) */}
                    {enableAuditTaskDrafts &&
                      !isReadOnlyMode &&
                      !isDraftsError &&
                      (isDataCollectionSub
                        ? !!getResponseValue(subQuestionId || '')
                        : subResponse?.result === QuestionResultType.FAIL) &&
                      (() => {
                        const subDraft = subQuestion.subQuestionId
                          ? getSubQuestionDraft(subQuestion.subQuestionId)
                          : undefined;
                        return (
                          <TaskDraftPanel
                            data-testid={`sub-question-draft-panel-${index}`}
                            audit={audit!}
                            questionId={currentQuestion!.questionId}
                            questionVersion={currentQuestion!.version?.toString() ?? ''}
                            subQuestionId={subQuestion.subQuestionId}
                            questionTitle={subQuestion.title}
                            defaultTask={subDraft ? undefined : subQuestion.taskDetails}
                            existingDraft={subDraft}
                            businessUnitId={getAuditBusinessUnitId()}
                            divisionName={audit!.divisionName}
                            onError={(msg) => setDraftErrorMessage(msg)}
                            isDataCollection={isDataCollectionSub}
                            headerText={
                              isDataCollectionSub
                                ? subDraft
                                  ? 'Edit Task for Sub-Question'
                                  : 'Create Task for Sub-Question'
                                : undefined
                            }
                            defaultExpanded={!isDataCollectionSub}
                          />
                        );
                      })()}
                  </SpaceBetween>
                </Box>
              </div>
            );
          })}
        </SpaceBetween>
      </ExpandableSection>
    );
  };

  /**
   * Handles file upload for question attachments
   * Uploads files to media service and associates them with the current question response
   * Updates audit status to 'in progress' after successful upload
   * @param {File[]} files - Array of files to upload
   * @param {string} [subQuestionId] - Optional sub-question ID for sub-question uploads
   */
  const handleFileUpload = async (files: File[], subQuestionId?: string) => {
    await handleFileUploadHelper(
      files,
      uploadfiles,
      getUserProgramId(),
      getUserId(),
      updateQuestionResponse,
      currentQuestion,
      currentQuestionResponse,
      subQuestionId,
      enableEnhancedResponseTypes,
    );
    updateAuditToInprogress(audit!, updateAudit, getUserId);
  };

  /**
   * Memoized function to retrieve comments for a question or sub-question
   * @param {string} [subQuestionId] - Optional sub-question ID; if not provided, gets main question comments
   * @returns {string} The comment text for the specified question/sub-question
   */
  const getComments = useCallback(
    (subQuestionId?: string) => {
      const commentKey = subQuestionId || currentQuestion?.questionId;
      return commentKey ? localComments[commentKey] || '' : '';
    },
    [localComments, currentQuestion],
  );

  /**
   * Memoized function to update comments for a question or sub-question
   * Updates local state only and marks data as unsaved
   * @param {string} value - The new comment text
   * @param {string} [subQuestionId] - Optional sub-question ID; if not provided, updates main question comments
   */
  const setComments = useCallback(
    (value: string, subQuestionId?: string) => {
      const commentKey = subQuestionId || currentQuestion?.questionId;
      if (commentKey) {
        setLocalComments((prev) => ({
          ...prev,
          [commentKey]: value,
        }));
        setDataSaved(false);
      }
    },
    [currentQuestion],
  );

  /**
   * ERT: Gets the local response value for a data-collection sub-question.
   * Falls back to the stored value from the sub-response.
   */
  const getResponseValue = useCallback(
    (subQuestionId: string): string => {
      return localResponseValues[subQuestionId] ?? '';
    },
    [localResponseValues],
  );

  /**
   * ERT: Updates local response value for a data-collection sub-question.
   * Used for TEXT/NUMERIC types — debounced save via the auto-save timer (10s).
   */
  const setResponseValue = useCallback((subQuestionId: string, value: string) => {
    setLocalResponseValues((prev) => ({
      ...prev,
      [subQuestionId]: value,
    }));
    setDataSaved(false);
  }, []);

  /**
   * ERT: Updates local response value AND triggers immediate save.
   * Used for selection-based types (SINGLE_SELECT, MULTI_SELECT, YES_NO, USER_ALIAS)
   * where the user made a deliberate discrete choice.
   */
  const pendingImmediateSaveRef = useRef(false);
  const setResponseValueImmediate = useCallback(
    (subQuestionId: string, value: string) => {
      setLocalResponseValues((prev) => ({
        ...prev,
        [subQuestionId]: value,
      }));
      setDataSaved(false);
      pendingImmediateSaveRef.current = true;
    },
    [],
  );

  /**
   * Handles NA reason selection for a question or sub-question.
   * Sets local state and triggers an immediate save.
   * @param {string} reasonId - The selected reason ID
   * @param {string} label - The selected reason label
   * @param {string} [subQuestionId] - Optional sub-question ID
   */
  const setNaReason = useCallback(
    (reasonId: string, label: string, subQuestionId?: string) => {
      const key = subQuestionId || currentQuestion?.questionId;
      if (key) {
        setLocalNaReasons((prev) => ({
          ...prev,
          [key]: { reasonId, label },
        }));
        setDataSaved(false);
      }
    },
    [currentQuestion],
  );

  /**
   * Clears the NA reason for a question or sub-question when answer changes away from NA.
   * @param {string} [subQuestionId] - Optional sub-question ID
   */
  const clearNaReason = useCallback(
    (subQuestionId?: string) => {
      const key = subQuestionId || currentQuestion?.questionId;
      if (key) {
        setLocalNaReasons((prev) => {
          const newReasons = { ...prev };
          delete newReasons[key];
          return newReasons;
        });
      }
    },
    [currentQuestion],
  );

  const questionContent = useMemo(
    () =>
      currentQuestion
        ? getQuestionContent(currentQuestion, getUserSelectedLanguage(), (error) =>
            setTranslationError(
              searchQuestionsApiStatus.isLoading === false ? error : '',
            ),
          )
        : null,
    [currentQuestion, getUserSelectedLanguage, searchQuestionsApiStatus.isLoading],
  );

  /**
   * Pre-built map of documents grouped by field name
   * Built once when mediaList or currentQuestion changes for efficiency
   */
  const documentsByField = useMemo(() => {
    return getDocumentsByField(
      currentQuestion,
      mediaList,
      listMediaByIdsApiStatus.isLoading,
    );
  }, [currentQuestion, mediaList, listMediaByIdsApiStatus.isLoading]);
  const feedbackMutationState = useMutationState({
    filters: {
      mutationKey: feedbackKeys.put({
        auditId: audit?.auditId ?? '',
        createdBy: getUserId(),
        questionId: currentQuestion?.questionId ?? '',
        businessUnitId: audit?.businessUnitId ?? '',
        divisionId: audit?.divisionName ?? '',
      }),
    },
  }).at(-1);

  const { data: feedback, isLoading: isLoadingFeedback } = useFeedbackQuery({
    auditId: audit?.auditId ?? '',
    createdBy: getUserId(),
    questionId: currentQuestion?.questionId ?? '',
    businessUnitId: audit?.businessUnitId ?? '',
    divisionId: audit?.divisionName ?? '',
  });

  /** Fetch task drafts/assigned for the current question (gated by feature flag)
   * we have to fetch assigned tasks for re-opened audits
   */

  const { data: tasksData, isError: isDraftsError } = useListTasksQuery(
    {
      businessUnitId: getAuditBusinessUnitId(),
      questionId: currentQuestion?.questionId ?? '',
      auditId: audit?.auditId ?? '',
      taskStatus: [TaskStatus.DRAFT, TaskStatus.ASSIGNED],
    },
    {
      enabled: enableAuditTaskDrafts && !!currentQuestion?.questionId && !!audit,
      refetchInterval: enableMultiAssignee ? POLL_INTERVAL_MS : undefined,
      refetchIntervalInBackground: false,
    },
  );

  const draftTasks = useMemo<Task[]>(() => tasksData?.tasks ?? [], [tasksData]);

  const mainQuestionDraft = useMemo(() => {
    const draft = draftTasks.find(
      (t) => t.questionId === currentQuestion?.questionId && !t.subQuestionId,
    );
    return draft;
  }, [draftTasks, currentQuestion?.questionId]);

  const getSubQuestionDraft = useCallback(
    (subQuestionId: string) =>
      draftTasks.find(
        (t) =>
          t.questionId === currentQuestion?.questionId &&
          t.subQuestionId === subQuestionId,
      ),
    [draftTasks, currentQuestion?.questionId],
  );

  // Early return AFTER all hooks to satisfy React Rules of Hooks
  if (!audit) {
    return <></>;
  }

  // Main component render
  return (
    /* Main container with scrollable content */
    <div>
      {/* Show upload progress when files are being uploaded */}
      <UploadProgressIndicator
        data-testid="upload-progress-indicator"
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />

      {/* Display API status messages and errors */}
      <ApiStatusFlashBar
        data-testid="api-status-flash-bar"
        apiStatus={[
          updateQuestionResponseApiStatus, // Question response update status
          uploadMediaApiStatus, // Media upload status
          deleteMediaApiStatus, // Media deletion status
        ]}
        clearMessages={() => {
          resetStatus(); // Clear question response status
          clearMediaAllApiStatus(); // Clear all media-related statuses
        }}
      />

      {/* Show error when task drafts fail to load */}
      {isDraftsError && (
        <Box margin={{ bottom: 's' }}>
          <Alert data-testid="drafts-error-alert" type="warning">
            Unable to load task drafts. Task drafts will not be available for this
            question.
          </Alert>
        </Box>
      )}
      {draftErrorMessage && (
        <Alert
          data-testid="draft-save-error-alert"
          type="error"
          dismissible
          onDismiss={() => setDraftErrorMessage(null)}
        >
          {draftErrorMessage}
        </Alert>
      )}

      {/* Show empty state when filter results in no questions */}
      {displayedQuestions.length === 0 &&
        !searchQuestionsApiStatus.isLoading &&
        (filteredQuestionIds !== null || auditQuestionsData.questions.length === 0) && (
          <Box
            data-testid="no-questions-filtered"
            textAlign="center"
            padding={{ vertical: 'xxl' }}
          >
            <SpaceBetween size="m" direction="vertical" alignItems="center">
              <StatusIndicator type="info">
                No questions match the selected filter
              </StatusIndicator>
              <Box color="text-body-secondary">
                Try selecting a different filter or clear the filter to see all questions
              </Box>
            </SpaceBetween>
          </Box>
        )}

      {/* Main content - only render if current question exists */}
      {currentQuestion && displayedQuestions.length > 0 && (
        <div data-testid="question-content" ref={(el) => (questionRefs.current = el)}>
          <SpaceBetween data-testid="question-spacer" size="l" direction="vertical">
            {translationError && (
              <Alert data-testid="translation-error-alert" type="error">
                Error : {translationError}
              </Alert>
            )}
            {/* Question header with ID and title */}
            <div className="question-header-flex">
              <TextContent data-testid="question-header">
                <p className="question-info">
                  Question ID: <strong>{currentQuestion?.questionId}</strong>
                  Question Version: <strong>{currentQuestion?.version}</strong>
                  Assigned Score: <strong>{currentQuestion?.auditScore}</strong>
                </p>
                <h4>Question Overview:</h4>
                <p>{questionContent?.questionDetails?.title}</p>
              </TextContent>
              {!audit || !currentQuestion || isLoadingFeedback ? null : (
                <div className="question-header-button-wrapper">
                  <Popover
                    ref={feedbackPopoverRef}
                    key={`feedback-popover-${audit.auditId}-${currentQuestion.questionId}`}
                    dismissButton={false}
                    header="Feedback"
                    size="large"
                    triggerType="custom"
                    content={
                      <FeedbackForm
                        key={`feedback-popover-${audit.auditId}-${currentQuestion.questionId}`}
                        auditId={audit.auditId}
                        divisionId={audit.divisionName}
                        questionId={currentQuestion.questionId}
                        businessUnitId={audit.businessUnitId ?? ''}
                        createdBy={getUserId()}
                        auditName={audit.name}
                        templateName={audit.templateName ?? ''}
                        location={audit.location}
                        questionTitle={questionContent?.questionDetails?.title ?? ''}
                        questionVersion={String(currentQuestion.version)}
                        onClose={() => {
                          // this closes the popover by focusing outside of the popover basically
                          feedbackPopoverRef.current?.focus();
                        }}
                      />
                    }
                  >
                    <Button
                      iconName="contact"
                      disabled={feedbackMutationState?.status === 'pending'}
                      loading={feedbackMutationState?.status === 'pending'}
                    >
                      {feedback ? 'Update Feedback' : 'Feedback'}
                    </Button>
                  </Popover>
                </div>
              )}
            </div>

            {/* Question tags display */}
            <SpaceBetween data-testid="question-tags" size="xs" direction="horizontal">
              {currentQuestion?.tagsIncluded &&
                /* Convert tags object to badge components */
                Object.entries(currentQuestion.tagsIncluded).reduce(
                  (acc: JSX.Element[], [tagGroupName, tagValues]: [string, string[]]) => {
                    // Create badges for each tag value
                    const badges = tagValues.map((data: string, index: number) => {
                      const sanitizedTagGroupName = tagGroupName.replace(
                        /[^a-zA-Z0-9]/g,
                        '-',
                      );
                      return (
                        <Badge
                          data-testid={`question-tag-${sanitizedTagGroupName}-${index}`}
                          key={`${tagGroupName}-${index}`}
                        >
                          {tagGroupName}:{data}
                        </Badge>
                      );
                    });
                    // Accumulate all badges into single array
                    return [...acc, ...badges];
                  },
                  [],
                )}
            </SpaceBetween>

            {/* Question details sections */}
            {questionContent?.questionDetails?.questionText && (
              <div data-testid="question-description">
                <TextContent>
                  {' '}
                  <h4>Audit Question:</h4>
                </TextContent>

                <TextEditor
                  readOnly
                  value={questionContent?.questionDetails.questionText}
                  initialFileMetadata={documentsByField['questionText'] || []}
                  mediaResponse={
                    currentQuestion?.mediaIdsMap?.questionText
                      ? mediaList.filter((m) =>
                          currentQuestion.mediaIdsMap!.questionText!.includes(
                            m.media.mediaId,
                          ),
                        )
                      : []
                  }
                />
              </div>
            )}
            {questionContent?.questionDetails?.auditInstruction && (
              <div data-testid="audit-instruction">
                <TextContent>
                  <h4>Audit Instruction:</h4>
                </TextContent>
                <TextEditor
                  readOnly
                  value={questionContent?.questionDetails.auditInstruction}
                  initialFileMetadata={documentsByField['auditInstruction'] || []}
                  mediaResponse={
                    currentQuestion?.mediaIdsMap?.auditInstruction
                      ? mediaList.filter((m) =>
                          currentQuestion.mediaIdsMap!.auditInstruction!.includes(
                            m.media.mediaId,
                          ),
                        )
                      : []
                  }
                />
              </div>
            )}
            {questionContent?.questionDetails?.passCriteria && (
              <div data-testid="pass-criteria">
                <TextContent>
                  <h4>Pass Criteria:</h4>
                </TextContent>
                <TextEditor
                  readOnly
                  value={questionContent?.questionDetails.passCriteria}
                  initialFileMetadata={documentsByField['passCriteria'] || []}
                  mediaResponse={
                    currentQuestion?.mediaIdsMap?.passCriteria
                      ? mediaList.filter((m) =>
                          currentQuestion.mediaIdsMap!.passCriteria!.includes(
                            m.media.mediaId,
                          ),
                        )
                      : []
                  }
                />
              </div>
            )}
            {questionContent?.questionDetails?.failCriteria && (
              <div data-testid="fail-criteria">
                <TextContent>
                  <h4>Fail Criteria:</h4>
                </TextContent>
                <TextEditor
                  readOnly
                  value={questionContent?.questionDetails.failCriteria}
                  initialFileMetadata={documentsByField['failCriteria'] || []}
                  mediaResponse={
                    currentQuestion?.mediaIdsMap?.failCriteria
                      ? mediaList.filter((m) =>
                          currentQuestion.mediaIdsMap!.failCriteria!.includes(
                            m.media.mediaId,
                          ),
                        )
                      : []
                  }
                />
              </div>
            )}
            {questionContent?.questionDetails?.justification && (
              <div data-testid="justification">
                <TextContent>
                  <h4>Justification:</h4>
                </TextContent>
                <TextEditor
                  readOnly
                  value={questionContent?.questionDetails.justification}
                  initialFileMetadata={documentsByField['justification'] || []}
                  mediaResponse={
                    currentQuestion?.mediaIdsMap?.justification
                      ? mediaList.filter((m) =>
                          currentQuestion.mediaIdsMap!.justification!.includes(
                            m.media.mediaId,
                          ),
                        )
                      : []
                  }
                />
              </div>
            )}
            {/* Task details are hidden in perform audit view */}

            {currentQuestion?.docLinks && currentQuestion?.docLinks.length > 0 && (
              <ExpandableSection
                data-testid="sop-section"
                variant="default"
                headerText="Standard Operating Procedures:"
                defaultExpanded
              >
                <Table
                  data-testid="sop-table"
                  columnDefinitions={[
                    { id: 'title', header: 'Title', cell: (item) => item.title },
                    {
                      id: 'link',
                      header: 'Link',
                      cell: (item) => (
                        <a href={item.link} target="_blank" rel="noopener noreferrer">
                          {item.link}
                        </a>
                      ),
                    },
                  ]}
                  items={currentQuestion.docLinks}
                />
              </ExpandableSection>
            )}
            {renderSubQuestions()}
            <TextContent>
              <h4>Audit Question Response:</h4>
            </TextContent>
            <FormField data-testid="response-field" stretch>
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <AuditQuestionActionButton
                  data-testid="audit-question-action-button"
                  ukey={currentQuestion.questionId}
                  disable={isReadOnlyMode}
                  isLoading={updateQuestionResponseApiStatus.isLoading}
                  selectedAnswer={getMainQuestionStatus(
                    currentQuestionResponse,
                    currentQuestion,
                  )}
                  showAll={
                    enableEnhancedResponseTypes
                      ? !!questionContent?.subQuestions?.some(
                          (sq) =>
                            !sq.responseType ||
                            sq.responseType === ResponseType.PASS_FAIL,
                        )
                      : !!questionContent?.subQuestions?.length
                  }
                  onAnswerClick={(result?: QuestionResultType) =>
                    updateQuestionAnswer(currentQuestion.questionId, result)
                  }
                  disabledReason={disableReason}
                />
                {enableMultiAssignee && currentQuestionResponse?.responseResult && (
                  <LastUpdatedIndicator
                    lastUpdatedBy={currentQuestionResponse.lastUpdatedBy}
                    lastUpdatedAt={currentQuestionResponse.lastUpdatedAt}
                  />
                )}
              </SpaceBetween>
            </FormField>
            {/* NA Reason dropdown - shown when result is NA and reasons are configured */}
            {enableDivisionalConfiguration &&
              naReasons.length > 0 &&
              (getMainQuestionStatus(currentQuestionResponse, currentQuestion) ===
                QuestionResultType.NA ||
                currentQuestionResponse?.responseResult === QuestionResultType.NA) &&
              shouldShowNaReasonDropdown('main', {
                mainResponseResult: currentQuestionResponse?.responseResult,
                subResponses: currentQuestionResponse?.subResponses ?? [],
                localNaReasons,
              }) && (
                <FormField
                  data-testid="na-reason-field"
                  label="Not Applicable Reason"
                  errorText={
                    !localNaReasons[currentQuestion.questionId] &&
                    !currentQuestionResponse?.response?.naReasonId &&
                    !isReadOnlyMode
                      ? 'Please select a Not Applicable reason before closing the audit'
                      : undefined
                  }
                >
                  {isReadOnlyMode ? (
                    <Box>
                      {localNaReasons[currentQuestion.questionId]?.label ||
                        currentQuestionResponse?.response?.naReason ||
                        '-'}
                    </Box>
                  ) : (
                    <Select
                      data-testid="na-reason-select"
                      selectedOption={
                        localNaReasons[currentQuestion.questionId]
                          ? {
                              label: localNaReasons[currentQuestion.questionId].label,
                              value: localNaReasons[currentQuestion.questionId].reasonId,
                            }
                          : currentQuestionResponse?.response?.naReasonId
                            ? {
                                label: currentQuestionResponse.response.naReason || '',
                                value: currentQuestionResponse.response.naReasonId,
                              }
                            : null
                      }
                      onChange={({ detail }) => {
                        if (detail.selectedOption.value && detail.selectedOption.label) {
                          setNaReason(
                            detail.selectedOption.value,
                            detail.selectedOption.label,
                          );
                        }
                      }}
                      options={naReasons.map((r) => ({
                        label: r.label!,
                        value: r.reasonId!,
                      }))}
                      placeholder="Select Not Applicable reason"
                      filteringType="auto"
                    />
                  )}
                </FormField>
              )}
            <SpaceBetween data-testid="response-actions-spacer" size="l">
              <FormField
                data-testid="comments-field"
                label={
                  <FieldRequirementLabel
                    fieldName="Comments"
                    requireOnFail={currentQuestion?.requireCommentOnFail}
                    requireOnPass={currentQuestion?.requireCommentOnPass}
                    result={currentQuestionResponse?.responseResult}
                  />
                }
              >
                <CompositionSafeTextarea
                  data-testid="comments-textarea"
                  placeholder="Add any additional comments..."
                  readOnly={isReadOnlyMode}
                  value={getComments()}
                  onChange={(e) => {
                    setComments(e.detail.value, undefined);
                  }}
                />
              </FormField>

              <FormField
                data-testid="attachments-field"
                label={
                  <FieldRequirementLabel
                    fieldName="Attachments"
                    requireOnFail={currentQuestion?.requireEvidenceOnFail}
                    requireOnPass={currentQuestion?.requireEvidenceOnPass}
                    result={currentQuestionResponse?.responseResult}
                  />
                }
              >
                {!isReadOnlyMode && (
                  <FileUpload
                    data-testid="file-upload"
                    accept={AUDIT_UPLOAD_ACCEPT_TYPES}
                    onChange={({ detail }) => handleFileUpload(detail.value)}
                    value={[]}
                    i18nStrings={{
                      uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
                      dropzoneText: (e) =>
                        e ? 'Drop files to upload' : 'Drop file to upload',
                      removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                      limitShowFewer: 'Show fewer files',
                      limitShowMore: 'Show more files',
                      errorIconAriaLabel: 'Error',
                    }}
                    multiple
                    showFileSize
                    showFileThumbnail
                  />
                )}
              </FormField>
              <ImageGallery
                data-testid="image-gallery"
                items={getImageFiles(currentQuestion, currentQuestionResponse)}
                readOnly={isReadOnlyMode}
                loading={deleteMediaApiStatus.isLoading}
                onDelete={(mediaId) =>
                  deleteMedia({
                    programId: getUserProgramId(),
                    deletedBy: getUserId(),
                    mediaId,
                  })
                }
              />
              <FileDisplay
                data-testid="file-display"
                files={getDocumentFiles(currentQuestion, currentQuestionResponse)}
                readonly={isReadOnlyMode}
                onRemove={(mediaId) =>
                  deleteMedia({
                    programId: getUserProgramId(),
                    deletedBy: getUserId(),
                    mediaId,
                  })
                }
              />
              {/* Task Draft Panel for main question - shown after comments/attachments on FAIL */}
              {enableAuditTaskDrafts &&
                !isReadOnlyMode &&
                !isDraftsError &&
                currentQuestionResponse?.responseResult === QuestionResultType.FAIL && (
                  <TaskDraftPanel
                    data-testid="main-question-draft-panel"
                    audit={audit}
                    questionId={currentQuestion.questionId}
                    questionVersion={currentQuestion.version?.toString() ?? ''}
                    questionTitle={questionContent?.questionDetails?.title}
                    defaultTask={
                      mainQuestionDraft ? undefined : questionContent?.taskDetails
                    }
                    existingDraft={mainQuestionDraft}
                    businessUnitId={getAuditBusinessUnitId()}
                    divisionName={audit.divisionName}
                    onError={(msg) => setDraftErrorMessage(msg)}
                    headerFontSize="heading-s"
                  />
                )}
            </SpaceBetween>
          </SpaceBetween>
        </div>
      )}
    </div>
  );
});

export default PerformAuditQuestionView;
